import React, { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { HttpService } from '@core/services';
import { SiteHttpService } from '@core/services/http';
import { Details } from '@shared/modules/details';
import { BreadcrumbsActions, SiteActions } from '@store/actions';
import { ProjectSelectors, SiteSelectors } from '@store/selectors';

type SiteDetailsRouteParams = {
  PSLocationCode?: string;
  projectBundleID?: string;
  pmProjectBundleID?: string;
};

export const SiteDetails: FC<RouteComponentProps> = (
  props: PropsWithChildren<RouteComponentProps>,
) => {
  const {
    match: { params },
  } = props;
  const routeParams = params as SiteDetailsRouteParams;

  const dispatch = useDispatch();
  const site = useSelector(SiteSelectors.getCurrentSite);
  const { currentProject, projectSite } = useSelector(
    ProjectSelectors.getProjectSummaryData,
  );

  const siteService = useMemo(
    () => HttpService.getHttpRequests(SiteHttpService),
    [],
  );

  const projectBundleID =
    routeParams.projectBundleID ?? routeParams.pmProjectBundleID;
  const isProjectContext = !!projectBundleID;
  const isCurrentProject =
    currentProject?.projectBundleID?.toString() === projectBundleID;
  const embeddedProjectSite =
    isProjectContext && isCurrentProject ? projectSite : undefined;
  const locationCode = routeParams.PSLocationCode
    ? routeParams.PSLocationCode
    : embeddedProjectSite?.PSLocationCode ??
      embeddedProjectSite?.MDGLocationCode;

  useEffect(() => {
    if (locationCode) {
      dispatch(SiteActions.resetTabsData.done(undefined));
      dispatch(
        SiteActions.setTabLoading.done({
          tabName: 'information',
          isLoading: true,
        }),
      );
      siteService
        .getSiteInformation(locationCode, !isProjectContext)
        .then(siteInfo => {
          if (!isProjectContext) {
            dispatch(BreadcrumbsActions.site.done(siteInfo));
          }
        })
        .catch(() =>
          dispatch(
            SiteActions.setTabError.done({
              tabName: 'information',
              hasError: true,
            }),
          ),
        );
    }
  }, [dispatch, isProjectContext, locationCode, siteService]);

  useEffect(
    () => (): void => {
      dispatch(SiteActions.resetTabsData.done(undefined));
    },
    [],
  );

  return (
    <Details
      fuzeID={isProjectContext}
      {...props}
      site={site?.id ? site : embeddedProjectSite}
    />
  );
};
